import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  CrossIcon,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  UploadFilledIcon,
} from '@honestica/curecall-ui'
import { AxiosResponse } from 'axios'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useLocale } from 'src/hooks/use-locale'
import { BatchImportTable } from './batch-import-table.component'
import { EBatchImportTier, getBatchImportTier } from './batch-import-errors.util'
import { useBatchImportStatus } from './use-batch-import-status.hook'
import { buildBatchImportCsv, downloadCsv } from './batch-import-csv.util'
import { getRecentBatchImport, saveRecentBatchImport } from './recent-batch-imports.util'

type BackendResponse = {
  success: boolean
  message: string
  dataCount: number
  interopLogIds?: number[]
  batchImportToken?: string
}

const BATCH_QUERY_PARAM = 'batch'

type Props = {
  project: { id: number }
  accept?: string
  postFunction: (projectId: number, formData: FormData) => Promise<AxiosResponse>
  className?: string
  emptyResultMessage?: string
  onResultVisibilityChange?: (isVisible: boolean) => void
}

type BackendResponseStatusAlertProps = {
  response: BackendResponse
  projectId: number
  resetHandler: () => void
  className?: string
  emptyResultMessage?: string
}

const BatchImportProgress: FC<{ projectId: number; response: BackendResponse }> = ({
  projectId,
  response,
}) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { results, expectedCount, processedCount, isComplete, isForbidden, isExpired, refresh } =
    useBatchImportStatus({
      projectId,
      batchImportToken: response.batchImportToken,
      interopLogIds: response.interopLogIds,
    })

  useEffect(() => {
    Object.values(results).forEach((result) => {
      if (getBatchImportTier(result) === EBatchImportTier.ERROR) {
        console.error(`Batch import log ${result.interopLogId} failed`, result.errors)
      }
    })
  }, [results])

  if (!response.batchImportToken) return null
  const { batchImportToken } = response
  const rowIds = response.interopLogIds ?? Object.keys(results).map(Number)

  const handleExportCsv = () => {
    const importDate = dayjs(getRecentBatchImport(batchImportToken)?.createdAt ?? Date.now())
    downloadCsv(
      `import-${importDate.format('YYYY-MM-DD-HHmm')}.csv`,
      buildBatchImportCsv({ t, rowIds, results, projectId, locale }),
    )
  }

  if (isForbidden) {
    return (
      <p className="c-mt-6 c-text-red-900" role="alert">
        {t('components.file-uploader.batch-import-forbidden')}
      </p>
    )
  }

  if (isExpired) {
    return (
      <p className="c-mt-6" role="status">
        {t('pages.upload.import-expired')}
      </p>
    )
  }

  return (
    <div className="c-mt-6" role="status">
      {response.interopLogIds && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={expectedCount}
          aria-valuenow={processedCount}
          className="c-w-full c-h-2 c-rounded-full c-bg-grey-200 c-overflow-hidden"
        >
          <div
            className="c-h-full c-rounded-full c-bg-primary c-transition-all"
            style={{ width: `${(processedCount / expectedCount) * 100}%` }}
          />
        </div>
      )}
      <div className="c-flex c-items-center c-justify-between c-gap-4 c-mt-4">
        <p className="c-text-sm c-m-0">
          {!response.interopLogIds
            ? t('components.file-uploader.batch-import-partial', { count: processedCount })
            : isComplete
              ? t('components.file-uploader.batch-import-complete', { count: expectedCount })
              : t('components.file-uploader.batch-import-progress', {
                  processed: processedCount,
                  total: expectedCount,
                })}
        </p>
        <div className="c-flex c-gap-2">
          {!isComplete && (
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              size={ECurecallSize.SMALL}
              onClick={refresh}
            >
              {t('components.file-uploader.batch-import-refresh')}
            </CurecallButton>
          )}
          <CurecallButton
            appearance={ECurecallButtonAppearance.SECONDARY}
            size={ECurecallSize.SMALL}
            onClick={handleExportCsv}
          >
            {t('components.file-uploader.batch-import-export-csv')}
          </CurecallButton>
        </div>
      </div>
      <BatchImportTable projectId={projectId} rowIds={rowIds} results={results} />
    </div>
  )
}

const SVG_PATHS = {
  success:
    'M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z',
  error:
    'M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z',
}

const BackendResponseStatusAlert: FC<BackendResponseStatusAlertProps> = ({
  response,
  projectId,
  resetHandler,
  className,
  emptyResultMessage,
}) => {
  const { t } = useTranslation()

  let errorMessage: string
  switch (response.message) {
    case 'Unsupported file type':
      errorMessage = t('components.file-uploader.error-unsupported-file-type')
      break
    case 'File too large':
      errorMessage = t('components.file-uploader.error-file-too-large')
      break
    case 'data not stored':
      errorMessage = t('components.file-uploader.error-data-not-stored')
      break
    default:
      errorMessage = t('components.file-uploader.if-problem-persists')
  }
  const successMessage =
    response.dataCount === 0 && emptyResultMessage && !response.batchImportToken
      ? emptyResultMessage
      : t('components.file-uploader.patient-data-count', { dataCount: response?.dataCount })
  const alertLayout = response.success
    ? [
        'c-bg-teal-100',
        'c-border-t-4',
        'c-border-teal-500',
        'c-text-teal-500',
        t('components.file-uploader.file-successfully-imported'),
        successMessage,
        SVG_PATHS.success,
      ]
    : [
        'c-bg-red-100',
        'c-border-t-4',
        'c-border-red-500',
        'c-text-red-900',
        t('components.file-uploader.error-during-import'),
        errorMessage,
        SVG_PATHS.error,
      ]

  // The per-line report replaces the success banner; an empty batch has no report, so it keeps the banner
  const hasReport = response.success && !!response.batchImportToken && response.interopLogIds?.length !== 0

  return (
    <div className={className}>
      {!hasReport && (
        <div
          className={`${alertLayout[0]} ${alertLayout[1]} c-rounded-b ${alertLayout[2]} c-px-4 c-py-3 c-shadow-md c-flex`}
          role="alert"
        >
          <div className="c-py-1">
            <svg
              className={`${alertLayout[3]} c-fill-current c-h-6 c-w-6 c-mr-4`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d={alertLayout[6]} />
            </svg>
          </div>
          <div>
            <p className="c-font-bold">{alertLayout[4]}</p>
            <p className="c-text-sm">{alertLayout[5]}</p>
          </div>
        </div>
      )}
      {hasReport && (
        <BatchImportProgress key={response.batchImportToken} projectId={projectId} response={response} />
      )}
      <div className="c-flex c-justify-center c-items-center c-space-x-8 c-py-8">
        <CurecallButton appearance={ECurecallButtonAppearance.PRIMARY} onClick={resetHandler}>
          {t('components.file-uploader.import-another-file')}
        </CurecallButton>
      </div>
    </div>
  )
}

const FileUploaderComponent: FC<Props> = ({
  project,
  accept,
  postFunction,
  className,
  emptyResultMessage,
  onResultVisibilityChange,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [backendResponse, setBackendResponse] = useState<BackendResponse | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  let _isUploadingPossible = true // Used as a local state to prevent the second click of a fast double-clic to
  // be registered before the useState had time to re-render the upload button to a `loading` state.
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()

  // Keep the view in sync with the URL: reopening a batch (refresh, bookmark, recent imports link, back button)
  const batchParam = searchParams.get(BATCH_QUERY_PARAM)
  useEffect(() => {
    if (!batchParam) {
      if (backendResponse?.batchImportToken) {
        setBackendResponse(null)
        onResultVisibilityChange?.(false)
      }
      return
    }
    if (batchParam === backendResponse?.batchImportToken) return
    const stored = getRecentBatchImport(batchParam)
    setBackendResponse({
      success: true,
      message: '',
      dataCount: stored?.dataCount ?? 0,
      interopLogIds: stored?.interopLogIds,
      batchImportToken: batchParam,
    })
    onResultVisibilityChange?.(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchParam])

  const setUploadingState = (value: boolean): void => {
    setIsUploading(value)
    _isUploadingPossible = !value
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i]?.kind === 'file') {
          const file = e.dataTransfer.items[i]?.getAsFile()
          setSelectedFile(file ?? null)
          break
        }
      }
    }
  }, [])

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      setSelectedFile(files[0] || null)
    }
  }

  const handleFileReset = () => {
    const fileInput: HTMLInputElement | null = document.getElementById('hiddenFileInput') as HTMLInputElement

    if (fileInput) {
      fileInput.value = '' // this clears the input file
    }

    setBackendResponse(null)
    setSelectedFile(null)
    setSearchParams({}, { replace: true })
    onResultVisibilityChange?.(false)
  }

  const handleUploadClick = async () => {
    if (!selectedFile || !_isUploadingPossible) return

    setUploadingState(true)

    const formData = new FormData()
    formData.append('file', selectedFile)

    let response
    try {
      response = await postFunction(project.id, formData)
    } catch {
      response = { data: { success: false, message: t('components.file-uploader.upload-error-title') } }
    }

    setBackendResponse(response.data)
    if (response.data.batchImportToken) {
      const { batchImportToken, interopLogIds, dataCount } = response.data
      saveRecentBatchImport({
        batchImportToken,
        projectId: project.id,
        createdAt: Date.now(),
        interopLogIds,
        dataCount,
      })
      setSearchParams({ [BATCH_QUERY_PARAM]: batchImportToken }, { replace: true })
    }
    onResultVisibilityChange?.(true)
    setUploadingState(false)
  }

  if (backendResponse) {
    return (
      <BackendResponseStatusAlert
        className={className}
        response={backendResponse}
        projectId={project.id}
        resetHandler={handleFileReset}
        emptyResultMessage={emptyResultMessage}
      />
    )
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-center h-screen">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="c-flex c-items-center c-justify-center c-border-2 c-border-dotted c-border-gray-500 c-bg-gray-200 c-rounded-lg c-h-80 c-w-full"
        >
          {selectedFile ? (
            <div className="c-flex c-flex-col c-items-center c-justify-center">
              <div className="c-flex c-items-center c-space-x-2">
                <p>{t('components.file-uploader.selected-file')}</p>
                <p className="c-inline-block c-px-2 c-py-1 c-text-sm c-bg-white c-rounded-lg c-shadow">
                  {selectedFile.name}
                  <button
                    className="c-ml-2 c-align-middle c-text-red-700 cursor-pointer"
                    onClick={handleFileReset}
                    aria-label={t('components.file-uploader.delete-file-selected')}
                  >
                    <CrossIcon className="tw-w-3 tw-h-3" />
                  </button>
                </p>
              </div>
              <CurecallButton
                className="c-mt-5"
                color="primary"
                size={ECurecallSize.SMALL}
                onClick={handleUploadClick}
                isLoading={isUploading}
              >
                {t('components.file-uploader.send')}
              </CurecallButton>
            </div>
          ) : (
            <button
              className="c-flex c-flex-col c-items-center c-justify-center"
              aria-label={t('components.file-uploader.import-a-file')}
              onClick={() => document.getElementById('hiddenFileInput')?.click()}
            >
              <div className="c-rounded-full c-bg-primary c-p-3.5 c-mb-4">
                <UploadFilledIcon className="tw-text-white tw-w-8 tw-h-8" />
              </div>
              <p className="c-font-nexa c-text-lg c-m-0 c-p-0">
                {t('components.file-uploader.click-here-to-select-a-file')}
              </p>
              <p className="c-font-nexa c-text-md c-m-0 c-p-0">
                {t('components.file-uploader.or-drag-and-drop')}
              </p>
            </button>
          )}
        </div>
        <input
          type="file"
          id="hiddenFileInput"
          accept={accept}
          onChange={handleFileUpload}
          className="c-hidden"
        />
      </div>
    </div>
  )
}

export default FileUploaderComponent
